import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { FileText, Download, Trash2, Upload } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import PatientSelect from "../components/PatientSelect";

const DOC_CATS = ["referto RX", "risonanza", "prescrizione", "certificato", "documento", "altro"];

export default function Documents() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [patient, setPatient] = useState(null);
  const [uploadPatient, setUploadPatient] = useState(null);
  const [cat, setCat] = useState("referto RX");
  const [desc, setDesc] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const params = {};
    if (patient) params.patient_id = patient.id;
    const { data } = await api.get("/documents", { params });
    setDocs(data);
  }, [patient]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!uploadPatient) { toast.error("Seleziona prima il paziente"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("patient_id", uploadPatient.id);
      fd.append("category", cat);
      fd.append("description", desc);
      await api.post("/documents/upload", fd);
      toast.success("Documento caricato");
      setDesc("");
      load();
    } catch (err) { toast.error(apiError(err, "Errore upload")); } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/file/${d.id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Errore nel download"); }
  };

  const del = async (d) => {
    if (!window.confirm("Eliminare questo documento?")) return;
    await api.delete(`/documents/${d.id}`);
    load();
  };

  return (
    <div className="space-y-4" data-testid="documents-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-bold">Documenti</h1>
        <div className="ml-auto w-64"><PatientSelect value={patient} onChange={setPatient} data-testid="docs-filter-patient" /></div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <h3 className="font-heading font-bold mb-3">Carica nuovo documento</h3>
        <div className="flex flex-wrap items-center gap-2" data-testid="docs-upload-area">
          <div className="w-64"><PatientSelect value={uploadPatient} onChange={setUploadPatient} data-testid="docs-upload-patient" /></div>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger data-testid="docs-category" className="w-40 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{DOC_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <input data-testid="docs-description" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Descrizione" className="flex-1 min-w-40 rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.webp" onChange={upload} className="hidden" data-testid="docs-file-input" />
          <button data-testid="docs-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading || !uploadPatient}
            className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium disabled:opacity-50">
            <Upload size={14} /> {uploading ? "Caricamento..." : "Carica"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <div className="space-y-2" data-testid="documents-list">
          {docs.map((d) => (
            <div key={d.id} data-testid={`document-row-${d.id}`} className="flex items-center gap-3 border border-slate-100 rounded-xl px-4 py-3 hover:bg-slate-50/50">
              <FileText size={18} className="text-blue-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{d.filename}</p>
                <p className="text-xs text-slate-400">
                  <button className="text-blue-600 hover:underline" onClick={() => navigate(`/pazienti/${d.patient_id}`)}>{d.patient_name}</button>
                  {" · "}{d.category} · {new Date(d.created_at).toLocaleDateString("it-IT")} {d.description ? `· ${d.description}` : ""}
                </p>
              </div>
              <button data-testid={`document-download-${d.id}`} onClick={() => download(d)} className="p-2 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50"><Download size={16} /></button>
              <button data-testid={`document-delete-${d.id}`} onClick={() => del(d)} className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
            </div>
          ))}
          {docs.length === 0 && <p className="text-center text-slate-400 text-sm py-10" data-testid="documents-empty">Nessun documento</p>}
        </div>
      </div>
    </div>
  );
}
